import type { ConstructionObject, ConstructionTarget } from '@learnforge/demo-math/geometry';
import type { GeometryKey } from '../locales/keys';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ConstructionPreset {
  id: string;
  target: ConstructionTarget;
  labelKey: GeometryKey;
  advancedOnly: boolean;
  /** Given objects that form the starting configuration. Must satisfy the JSDoc
   *  requirements of `scriptedSteps(target)`. */
  given: readonly ConstructionObject[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function gpt(id: string, x: number, y: number): Extract<ConstructionObject, { kind: 'point' }> {
  return { kind: 'point', id, at: { x, y }, source: 'given' };
}

function gline(id: string, p1: string, p2: string): Extract<ConstructionObject, { kind: 'line' }> {
  return { kind: 'line', id, through: [p1, p2] };
}

function gcirc(
  id: string,
  center: string,
  through: string,
): Extract<ConstructionObject, { kind: 'circle' }> {
  return { kind: 'circle', id, center, through };
}

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------

/**
 * All 11 construction targets.  The `given` objects' ids MUST satisfy the
 * requirements documented in `scriptedSteps` so that `runSteps(given, steps)` +
 * `validateConstruction(target, objs, 1e-6).met === true`.
 */
export const CONSTRUCTION_PRESETS: readonly ConstructionPreset[] = [
  // ── 1. Perpendicular bisector ─────────────────────────────────────────────
  // Needs: points of[0], of[1].
  {
    id: 'perpendicular-bisector',
    target: { kind: 'perpendicular-bisector', of: ['A', 'B'] },
    labelKey: 'construct.target.perpendicular-bisector',
    advancedOnly: false,
    given: [gpt('A', 200, 230), gpt('B', 600, 230)],
  },

  // ── 2. Angle bisector ─────────────────────────────────────────────────────
  // Needs: points at, arms[0], arms[1]; lines la (at→arms[0]), lb (at→arms[1]).
  // scriptedSteps uses ix-lb-s1-1 = B (the +y intersection).
  // lb is vertical (x=V.x). s1=compass(V,A) intersects lb at V.y ± |VA|.
  // B must be at index 1 (larger y in screen coords) → B.y = V.y + |VA|.
  // Config: V=(400,230), A=(600,230), B=(400,430). |VA|=200. lb∩s1 at (400,30) idx0 and (400,430)=B idx1. ✓
  {
    id: 'angle-bisector',
    target: { kind: 'angle-bisector', at: 'V', arms: ['A', 'B'] },
    labelKey: 'construct.target.angle-bisector',
    advancedOnly: false,
    given: [
      gpt('V', 400, 230),
      gpt('A', 600, 230),
      gpt('B', 400, 430),
      gline('la', 'V', 'A'),
      gline('lb', 'V', 'B'),
    ],
  },

  // ── 3. Copy segment ───────────────────────────────────────────────────────
  // Needs: points from[0] (= ontoRayFrom), from[1]; line lray (through from[0]).
  // A = ontoRayFrom = R, B = far end of segment, Q gives direction of ray.
  {
    id: 'copy-segment',
    target: { kind: 'copy-segment', from: ['A', 'B'], ontoRayFrom: 'A' },
    labelKey: 'construct.target.copy-segment',
    advancedOnly: false,
    given: [
      gpt('A', 300, 230), // = R (ontoRayFrom)
      gpt('B', 300, 30), // B above A  (segment AB has length 200)
      gpt('Q', 500, 230), // Q to the right — lray is horizontal
      gline('lray', 'A', 'Q'),
    ],
  },

  // ── 4. Copy angle ─────────────────────────────────────────────────────────
  // Needs: points at, arms[0], arms[1], ontoRayFrom, Q (on base ray); line lray.
  // Copies 90° angle at V=(0,0), arms A=(4,0) and B=(0,4) onto ray R→Q.
  {
    id: 'copy-angle',
    target: { kind: 'copy-angle', at: 'V', arms: ['A', 'B'], ontoRayFrom: 'R' },
    labelKey: 'construct.target.copy-angle',
    advancedOnly: false,
    given: [
      gpt('V', 200, 300),
      gpt('A', 400, 300),
      gpt('B', 200, 100),
      gpt('R', 500, 300),
      gpt('Q', 550, 300),
      gline('lray', 'R', 'Q'),
    ],
  },

  // ── 5. Perpendicular at point ─────────────────────────────────────────────
  // Needs: points line[0], line[1], at (on line); line lline.
  // P is the midpoint of L1–L2 so that the compass arc reaches both ends.
  {
    id: 'perpendicular-at-point',
    target: { kind: 'perpendicular-at-point', line: ['L1', 'L2'], at: 'P' },
    labelKey: 'construct.target.perpendicular-at-point',
    advancedOnly: false,
    given: [
      gpt('L1', 200, 300),
      gpt('L2', 600, 300),
      gpt('P', 400, 300), // midpoint
      gline('lline', 'L1', 'L2'),
    ],
  },

  // ── 6. Perpendicular from point ───────────────────────────────────────────
  // Needs: points line[0], line[1], from; line lline.
  // P is external to the line, directly above the midpoint.
  {
    id: 'perpendicular-from-point',
    target: { kind: 'perpendicular-from-point', line: ['L1', 'L2'], from: 'P' },
    labelKey: 'construct.target.perpendicular-from-point',
    advancedOnly: false,
    given: [
      gpt('L1', 200, 300),
      gpt('L2', 600, 300),
      gpt('P', 400, 100), // external point above midpoint
      gline('lline', 'L1', 'L2'),
    ],
  },

  // ── 7a. Inscribed equilateral triangle (n=3) ──────────────────────────────
  // Needs: points O (center), P0 (on circle); circle C.
  {
    id: 'inscribed-triangle',
    target: { kind: 'inscribed-polygon', n: 3, inCircle: 'C' },
    labelKey: 'construct.target.inscribed-triangle',
    advancedOnly: true,
    given: [
      gpt('O', 400, 230),
      gpt('P0', 580, 230), // P0 on circle, radius = 180
      gcirc('C', 'O', 'P0'),
    ],
  },

  // ── 7b. Inscribed square (n=4) ────────────────────────────────────────────
  // Needs: points O, P0, Panti (antipode of P0); circle C.
  {
    id: 'inscribed-square',
    target: { kind: 'inscribed-polygon', n: 4, inCircle: 'C' },
    labelKey: 'construct.target.inscribed-square',
    advancedOnly: true,
    given: [
      gpt('O', 400, 230),
      gpt('P0', 580, 230),
      gpt('Panti', 220, 230), // antipode: O + (O - P0)
      gcirc('C', 'O', 'P0'),
    ],
  },

  // ── 7c. Inscribed regular hexagon (n=6) ───────────────────────────────────
  // Needs: points O (center), P0 (on circle); circle C.
  {
    id: 'inscribed-hexagon',
    target: { kind: 'inscribed-polygon', n: 6, inCircle: 'C' },
    labelKey: 'construct.target.inscribed-hexagon',
    advancedOnly: true,
    given: [
      gpt('O', 400, 230),
      gpt('P0', 550, 230), // radius = 150
      gcirc('C', 'O', 'P0'),
    ],
  },

  // ── 8. Tangent from external point ───────────────────────────────────────
  // Needs: O (circle center), Pon (on circle), from (external), M (midpoint of O–from); circle C.
  // Use O=(200,230), Pon=(300,230) r=100, P=(500,230), M=(350,230).
  {
    id: 'tangent-from-point',
    target: { kind: 'tangent-from-point', circle: 'C', from: 'P' },
    labelKey: 'construct.target.tangent-from-point',
    advancedOnly: true,
    given: [
      gpt('O', 200, 230),
      gpt('Pon', 300, 230),
      gcirc('C', 'O', 'Pon'),
      gpt('P', 500, 230),
      gpt('M', 350, 230), // midpoint of O and P
    ],
  },

  // ── 9. Incircle ───────────────────────────────────────────────────────────
  // Needs: points of[0], of[1], of[2]; lines lAB, lAC, lBC.
  // scriptedSteps uses ix-lAB-s1-1=B and ix-lAC-s1-1=C (the larger-y intersections).
  // Requires |AB|=|AC| and B/C at the larger-y (or larger-x) intersections.
  // Right isoceles: A=(150,80), B=(550,80), C=(150,480). |AB|=|AC|=400.
  // lAB horizontal: circle(A,400)∩lAB → ix-lAB-s1-0=(-250,80), ix-lAB-s1-1=(550,80)=B ✓
  // lAC vertical:  circle(A,400)∩lAC → ix-lAC-s1-0=(150,-320), ix-lAC-s1-1=(150,480)=C ✓
  {
    id: 'incircle',
    target: { kind: 'incircle', of: ['A', 'B', 'C'] },
    labelKey: 'construct.target.incircle',
    advancedOnly: true,
    given: [
      gpt('A', 150, 80),
      gpt('B', 550, 80),
      gpt('C', 150, 480),
      gline('lAB', 'A', 'B'),
      gline('lAC', 'A', 'C'),
      gline('lBC', 'B', 'C'),
    ],
  },

  // ── 10. Circumcircle ──────────────────────────────────────────────────────
  // Needs: points of[0], of[1], of[2].  No helper lines.
  {
    id: 'circumcircle',
    target: { kind: 'circumcircle', of: ['A', 'B', 'C'] },
    labelKey: 'construct.target.circumcircle',
    advancedOnly: true,
    given: [gpt('A', 200, 380), gpt('B', 600, 380), gpt('C', 350, 80)],
  },

  // ── 11a. Angle of measure 60° ─────────────────────────────────────────────
  // Needs: points atRayOrigin (=R), Q (on base ray); line lbase.
  {
    id: 'angle-60',
    target: { kind: 'angle-of-measure', deg: 60, atRayOrigin: 'R' },
    labelKey: 'construct.target.angle-60',
    advancedOnly: false,
    given: [gpt('R', 250, 300), gpt('Q', 450, 300), gline('lbase', 'R', 'Q')],
  },

  // ── 11b. Angle of measure 30° ─────────────────────────────────────────────
  {
    id: 'angle-30',
    target: { kind: 'angle-of-measure', deg: 30, atRayOrigin: 'R' },
    labelKey: 'construct.target.angle-30',
    advancedOnly: true,
    given: [gpt('R', 250, 300), gpt('Q', 450, 300), gline('lbase', 'R', 'Q')],
  },

  // ── 11c. Angle of measure 45° ─────────────────────────────────────────────
  {
    id: 'angle-45',
    target: { kind: 'angle-of-measure', deg: 45, atRayOrigin: 'R' },
    labelKey: 'construct.target.angle-45',
    advancedOnly: true,
    given: [gpt('R', 250, 300), gpt('Q', 450, 300), gline('lbase', 'R', 'Q')],
  },
];

// ---------------------------------------------------------------------------
// Validation utilities (used by tests and the experiment)
// ---------------------------------------------------------------------------

/** Returns true if all preset ids are unique. */
export function validatePresetIds(presets: readonly ConstructionPreset[]): boolean {
  const ids = new Set<string>();
  for (const p of presets) {
    if (ids.has(p.id)) return false;
    ids.add(p.id);
  }
  return true;
}

/** Returns true if every point id referenced by a target exists in the given objects. */
export function validatePresetPointRefs(preset: ConstructionPreset): boolean {
  const pointIds = new Set<string>(
    preset.given
      .filter((o): o is Extract<ConstructionObject, { kind: 'point' }> => o.kind === 'point')
      .map((o) => o.id),
  );

  const t = preset.target;
  const refs: string[] = [];

  switch (t.kind) {
    case 'perpendicular-bisector':
      refs.push(...t.of);
      break;
    case 'angle-bisector':
      refs.push(t.at, ...t.arms);
      break;
    case 'copy-segment':
      refs.push(...t.from, t.ontoRayFrom);
      break;
    case 'copy-angle':
      refs.push(t.at, ...t.arms, t.ontoRayFrom);
      break;
    case 'perpendicular-at-point':
      refs.push(...t.line, t.at);
      break;
    case 'perpendicular-from-point':
      refs.push(...t.line, t.from);
      break;
    case 'inscribed-polygon':
      // O and P0 are expected to be in given but not referenced by target directly
      break;
    case 'tangent-from-point':
      refs.push(t.from);
      break;
    case 'incircle':
      refs.push(...t.of);
      break;
    case 'circumcircle':
      refs.push(...t.of);
      break;
    case 'angle-of-measure':
      refs.push(t.atRayOrigin);
      break;
  }

  for (const ref of refs) {
    if (!pointIds.has(ref)) return false;
  }
  return true;
}
